import * as React from 'react';
import { v4 as uuid4 } from 'uuid';
import { compose } from 'recompose';
import { withStyles } from '@material-ui/core/styles';
import {
  WithStyles,
  Paper,
  TextField,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from '@material-ui/core';
import addSectionStyle from './addSectionStyle';
import translate from '@/app/utils/translate';
import {
  FormTemplate,
  FormTemplateSection,
  FormTemplateSectionUpdateCall
} from '@/app/components/TemplateComponents/types';
import { SectionAccess, TemplateType } from '@/app/components/TemplateComponents/constants';

type incomingProps = {
  handleModalClose: () => void;
  addTemplateSection: FormTemplateSectionUpdateCall;
  readyToEditTemplate: FormTemplate;
  newSectionAdded?: () => void;
  updateSection: FormTemplateSectionUpdateCall;
  section?: FormTemplateSection;
  editMode?: boolean;
  templateType?: TemplateType;
};

type StateType = {
  title: string;
  showValidationError: boolean;
  disabledSave: boolean;
  empAccess: boolean;
  onlyEmpAccess: boolean;
  mgrHrOnly: boolean;
  isLockable: boolean;
};

type PropsType = incomingProps & WithStyles<typeof addSectionStyle>;

const toAccess = (
  empAccess: boolean,
  onlyEmpAccess: boolean,
  mgrHrOnly: boolean,
): SectionAccess => {
  if (mgrHrOnly) return SectionAccess.Invisible;
  if (onlyEmpAccess) return SectionAccess.EmployeeOnly;
  if (empAccess) return SectionAccess.Everyone;
  return SectionAccess.ManagerHr;
};

const fromAccess = (access: SectionAccess) => {
  switch (access) {
    case SectionAccess.Everyone:
      return { empAccess: true, onlyEmpAccess: false, mgrHrOnly: false };
    case SectionAccess.EmployeeOnly:
      return { empAccess: false, onlyEmpAccess: true, mgrHrOnly: false };
    case SectionAccess.Invisible:
      return { empAccess: false, onlyEmpAccess: false, mgrHrOnly: true };
    default:
      return { empAccess: false, onlyEmpAccess: false, mgrHrOnly: false };
  }
};

class AddSection extends React.PureComponent<PropsType> {
  state: StateType = {
    title: '',
    showValidationError: false,
    disabledSave: true,
    empAccess: true,
    onlyEmpAccess: false,
    mgrHrOnly: false,
    isLockable: false,
  };

  constructor(props: any) {
    super(props);

    const { section, readyToEditTemplate } = this.props;

    // isTeam property is in Appraisal templates used as additional condition
    const handleIsTeamAccess = (access: boolean) => 'isTeam' in readyToEditTemplate ? !readyToEditTemplate.isTeam && access : access;

    this.state = {
      title: (section && section.name) || '',
      showValidationError: false,
      disabledSave: true,
      empAccess: handleIsTeamAccess(!!section && !!section.empAccess),
      onlyEmpAccess: handleIsTeamAccess(!!section && !!section.onlyEmpAccess),
      mgrHrOnly: !!section && !!section.mgrHrOnly,
      isLockable: !!section && !!section.isLockable,
    };
  }

  // To be moved to helper
  titleValidation = (title: string) => {
    if (title === '') {
      this.setState({ showValidationError: true });
      return false;
    } else {
      this.setState({ showValidationError: false });
      return true;
    }
  };

  changeTitle = (event: any) => {
    const target = event.target as HTMLTextAreaElement;
    this.setState({ title: target.value });
    if (target.value !== '') {
      this.setState({ showValidationError: false, disabledSave: false });
    }
  };

  handleSaveSection = () => {
    const {
      addTemplateSection,
      readyToEditTemplate,
      newSectionAdded,
      handleModalClose,
    } = this.props;
    const { title, empAccess, onlyEmpAccess, mgrHrOnly, isLockable } = this.state;
    const templateBeingEdited = readyToEditTemplate;

    if (!this.titleValidation(title)) {
      return;
    }
    addTemplateSection(templateBeingEdited.id, {
      id: uuid4(),
      name: title,
      templateId: readyToEditTemplate.id,
      empAccess: empAccess,
      onlyEmpAccess: onlyEmpAccess,
      mgrHrOnly: mgrHrOnly,
      isLockable: isLockable,
      index: templateBeingEdited.sections
        ? templateBeingEdited.sections.length + 1
        : 0,
    });
    handleModalClose();
    newSectionAdded();
  };

  handleSectionUpdate = () => {
    const { section, updateSection, readyToEditTemplate, handleModalClose } = this.props;
    const { title, empAccess, onlyEmpAccess, mgrHrOnly, isLockable } = this.state;

    if (!this.titleValidation(title)) {
      return;
    }
    const updatedSection = {
      id: section.id,
      components: section.components,
      name: title,
      templateId: readyToEditTemplate.id,
      empAccess: empAccess,
      onlyEmpAccess: onlyEmpAccess,
      mgrHrOnly: mgrHrOnly,
      isLockable: isLockable,
      index: section.index,
    };

    updateSection(readyToEditTemplate.id, updatedSection);
    handleModalClose();
  };

  employeeCanEdit = (setTrue: boolean) => {
    if (setTrue) {
      this.setState({ empAccess: true });
    } else {
      this.setState({ empAccess: !this.state.empAccess });
    }
    this.setState({ disabledSave: false });
  };
  onlyEmployeeCanEdit = () => {
    const { onlyEmpAccess } = this.state;
    this.setState({ onlyEmpAccess: !onlyEmpAccess }, () => {
      this.employeeCanEdit(true);
      this.setState({ disabledSave: false });
    });
  };

  changeAccess = (event: any) => {
    this.setState({
      ...fromAccess(event.target.value as SectionAccess),
      disabledSave: false,
    });
  };

  changeIsLockable = (event: any) => {
    this.setState({ isLockable: event.target.checked, disabledSave: false });
  };

  render() {
    const {
      classes,
      handleModalClose,
      editMode,
      readyToEditTemplate,
      templateType,
    } = this.props;
    const {
      title,
      empAccess,
      onlyEmpAccess,
      mgrHrOnly,
      isLockable,
      showValidationError,
      disabledSave,
    } = this.state;
    const disabledOnIsTeamCondition = 'isTeam' in readyToEditTemplate ? readyToEditTemplate.isTeam : false;

    return (
      <Paper className={classes.root}>
        <form className={classes.form}>
          <h3 className={classes.topTitle}>
            {editMode
              ? translate.t('edit_section')
              : translate.t('add_section')}
          </h3>
          <TextField
            label={translate.t('laTitle')}
            onChange={this.changeTitle}
            value={title}
            className={classes.spacing}
            required
            error={showValidationError}
            helperText={
              showValidationError ? translate.t('laThisRequired') : ' '
            }
          />
          {templateType === TemplateType.Process ? (
            <>
              <div className={classes.accessGroup}>
                <FormLabel className={classes.accessLegend}>
                  {translate.t('laSectionAccessLegend')}
                </FormLabel>
                <RadioGroup
                  value={toAccess(empAccess, onlyEmpAccess, mgrHrOnly)}
                  onChange={this.changeAccess}
                >
                  <FormControlLabel
                    value={SectionAccess.Everyone}
                    control={<Radio color="primary" />}
                    label={translate.t('laSectionAccessEveryone')}
                  />
                  <FormControlLabel
                    value={SectionAccess.ManagerHr}
                    control={<Radio color="primary" />}
                    label={translate.t('laSectionAccessManagerHr')}
                  />
                  <FormControlLabel
                    value={SectionAccess.EmployeeOnly}
                    control={<Radio color="primary" />}
                    label={translate.t('laSectionAccessEmployeeOnly')}
                  />
                  <FormControlLabel
                    value={SectionAccess.Invisible}
                    control={<Radio color="primary" />}
                    label={translate.t('laSectionAccessInvisible')}
                  />
                </RadioGroup>
              </div>
              <Divider />
              <div className={classes.accessGroup}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isLockable}
                      onChange={this.changeIsLockable}
                      color="primary"
                    />
                  }
                  label={translate.t('laSectionCanBeLocked')}
                />
              </div>
            </>
          ) : (
            <div className={classes.accessGroup}>
              <div className={classes.checkboxes}>
                <Checkbox
                  onClick={() => this.employeeCanEdit(false)}
                  checked={empAccess}
                  disabled={disabledOnIsTeamCondition || onlyEmpAccess}
                  color="primary"
                />
                {translate.t('employee_edit')}
              </div>
              <div className={classes.checkboxes}>
                <Checkbox
                  onClick={() => this.onlyEmployeeCanEdit()}
                  checked={onlyEmpAccess}
                  disabled={disabledOnIsTeamCondition}
                  color="primary"
                />
                {translate.t('only_employee_edit')}
              </div>
            </div>
          )}
          <Divider />
          <div className={classes.buttonsWrapper}>
            <Button color="primary" variant="text" onClick={handleModalClose}>
              {translate.t('laCancel')}
            </Button>
            {!editMode && (
              <Button
                color="primary"
                variant="contained"
                disabled={disabledSave}
                onClick={() => this.handleSaveSection()}
              >
                {translate.t('laSave')}
              </Button>
            )}
            {editMode && (
              <Button
                color="primary"
                variant="contained"
                disabled={disabledSave}
                onClick={() => this.handleSectionUpdate()}
              >
                {translate.t('laSaveChanges')}
              </Button>
            )}
          </div>
        </form>
      </Paper>
    );
  }
}

const enhance = compose<any, any>(withStyles(addSectionStyle));

export default enhance(AddSection);
